// Banner shown when the configured agents differ from the running ones.
// It names the change, and "Deploy now" asks first (agents restart and
// can't answer for about a minute) before opening the deploy dialog.

import { useState } from "react";
import { Rocket } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { useConfirm } from "@/components/ui/ConfirmModal";
import { useAuth } from "@/stores/auth";
import { useConfiguredAgents } from "@/api/agentConfig";
import { useCustomerAgents } from "@/api/customers";
import { useAgentDirectory } from "@/hooks/useAgentDirectory";
import { DeployModal } from "@/features/deploy/DeployModal";

export function DeployBanner() {
  const customerId = useAuth((s) => s.customerId) ?? "";
  const [showModal, setShowModal] = useState(false);
  const confirm = useConfirm();
  const { nameOf } = useAgentDirectory(customerId || null);

  const configured = useConfiguredAgents(customerId || null);
  const deployed = useCustomerAgents(customerId || null);

  // Only once both lists have answered: a list still loading (or one that
  // failed) is not "a change since the last deploy".
  if (!configured.isSuccess || !deployed.isSuccess) {
    return showModal ? <DeployModal onClose={() => setShowModal(false)} /> : null;
  }

  const configuredSet = new Set(configured.data.agents.map((a) => a.template));
  const deployedSet = new Set(deployed.data.agents.map((a) => a.template));
  const notRunning = [...configuredSet].filter((t) => !deployedSet.has(t));
  const stillRunning = [...deployedSet].filter((t) => !configuredSet.has(t));
  const changes = notRunning.length + stillRunning.length;

  if (changes === 0) {
    return showModal ? <DeployModal onClose={() => setShowModal(false)} /> : null;
  }

  const message =
    changes > 1
      ? `${changes} agent changes since the last deploy.`
      : notRunning.length === 1
        ? `${nameOf(notRunning[0])} isn't running yet.`
        : `${nameOf(stillRunning[0])} was removed but is still running.`;

  async function deployNow() {
    const ok = await confirm({
      title: "Deploy changes?",
      body: "Your agents restart and can't answer for about a minute.",
      confirmLabel: "Deploy",
      cancelLabel: "Not now",
    });
    if (ok) setShowModal(true);
  }

  return (
    <>
      <div
        role="status"
        className="flex min-h-10 shrink-0 flex-wrap items-center gap-x-3 gap-y-1 border-b border-[color:var(--c-glass-active-accent-border)] bg-selected px-4 py-1.5 md:px-8"
      >
        <span className="text-sm text-fg">{message}</span>
        <Button variant="primary" size="sm" className="ml-auto" onClick={() => void deployNow()}>
          <Rocket aria-hidden className="h-3.5 w-3.5" strokeWidth={1.75} />
          Deploy now
        </Button>
      </div>
      {showModal && <DeployModal onClose={() => setShowModal(false)} />}
    </>
  );
}
